import type { EntityPluginContext } from "@brains/plugins";
import { parseMarkdownWithFrontmatter } from "@brains/plugins";
import { SITE_CHANNELS, type SiteBuildStagingPayload } from "@brains/contracts";
import { getErrorMessage } from "@brains/utils/error";
import type { Logger } from "@brains/utils/logger";
import type { BlogPost } from "../schemas/blog-post";
import {
  blogPostFrontmatterSchema,
  blogPostSchema,
} from "../schemas/blog-post";
import type { BlogPostWithData } from "../datasources/blog-datasource";
import { generateRSSFeed } from "../rss/feed-generator";
import { promises as fs } from "fs";
import { join } from "path";

export function subscribeToSiteBuildStaging(
  context: EntityPluginContext,
  logger: Logger,
): void {
  context.messaging.subscribeExecution<
    SiteBuildStagingPayload,
    { success: boolean }
  >(SITE_CHANNELS.buildStaging, async (message) => {
    const payload = message.payload;
    try {
      logger.info(
        `Received site:build:staging event for ${payload.environment} environment`,
      );

      await generateRSSAfterBuild(context, logger, payload);
    } catch (error) {
      // Throwing here would be swallowed by the broadcast dispatcher and the
      // build would publish a generation with no feed. Report it instead so
      // the build fails and the previous generation stays active.
      logger.error("Failed to generate RSS feed", error);
      payload.reportFailure(
        `RSS feed generation failed: ${getErrorMessage(error)}`,
      );
    }
    return { success: true };
  });
}

async function generateRSSAfterBuild(
  context: EntityPluginContext,
  logger: Logger,
  payload: SiteBuildStagingPayload,
): Promise<void> {
  const isPreview = payload.environment === "preview";
  logger.info(
    `Auto-generating RSS feed after site build (${isPreview ? "all posts" : "published only"})`,
  );

  const allPosts: BlogPost[] = await context.entityService.listEntities(
    { entityType: "post", options: { limit: 1000 } },
    blogPostSchema,
  );

  const filteredPosts: BlogPostWithData[] = allPosts
    .filter(
      (p) =>
        isPreview ||
        (p.metadata.status === "published" && p.metadata.publishedAt),
    )
    .map((entity) => {
      const parsed = parseMarkdownWithFrontmatter(
        entity.content,
        blogPostFrontmatterSchema,
      );
      return {
        ...entity,
        frontmatter: parsed.metadata,
        body: parsed.content,
        url: payload.generateEntityUrl("post", entity.metadata.slug),
      };
    });

  if (filteredPosts.length === 0) {
    logger.info(
      `No ${isPreview ? "" : "published "}posts found, skipping RSS generation`,
    );
    return;
  }

  const siteUrl = payload.siteConfig.url ?? "https://example.com";
  const siteTitle = payload.siteConfig.title ?? "Blog";
  const siteDescription = payload.siteConfig.description ?? "Latest blog posts";

  const xml = generateRSSFeed(filteredPosts, {
    title: siteTitle,
    description: siteDescription,
    link: siteUrl,
    language: "en-us",
    includeAllPosts: isPreview,
  });

  const feedPath = join(payload.outputDir, "feed.xml");
  await fs.writeFile(feedPath, xml, "utf-8");

  logger.info(
    `RSS feed generated successfully with ${filteredPosts.length} posts at ${feedPath}`,
  );
}
